import { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserCircle, Search, Send } from "lucide-react";

interface Funcionario { id: number; nome: string; }
interface DiaEspelho { data: string; minutosTrabalhados: number; minutosPrevistos: number; minutosSaldo: number; tipoDiaTexto: string; }
interface Espelho { saldoFinalMin: number; dias: DiaEspelho[]; }
interface Comprovante { nsrFormatado: string; tipoBatidaTexto: string; dtHoraMarcacao: string; }

const hm = (m: number) => `${m < 0 ? "-" : ""}${String(Math.floor(Math.abs(m) / 60)).padStart(2, "0")}:${String(Math.abs(m) % 60).padStart(2, "0")}`;
const dt = (s: string) => (s ? new Date(s).toLocaleString("pt-BR") : "—");
const hoje = new Date();
const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().slice(0, 10);
const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().slice(0, 10);

const TIPOS = [{ v: 1, t: "Inclusão de batida" }, { v: 2, t: "Abono de falta" }, { v: 3, t: "Atestado" }];

export default function MeuPontoPage() {
  const [me, setMe] = useState<Funcionario | null>(null);
  const [semVinculo, setSemVinculo] = useState(false);
  const [carregando, setCarregando] = useState(true);

  const [inicio, setInicio] = useState(primeiroDia);
  const [fim, setFim] = useState(ultimoDia);
  const [espelho, setEspelho] = useState<Espelho | null>(null);
  const [comprovantes, setComprovantes] = useState<Comprovante[]>([]);
  const [ajuste, setAjuste] = useState({ DtReferencia: new Date().toISOString().slice(0, 10), ITipoAjuste: 3, SJustificativa: "" });

  useEffect(() => {
    apiClient.get<Funcionario>("/api/rh/ponto/meu/funcionario")
      .then((r) => { setMe(r.data); return apiClient.get<Comprovante[]>("/api/rh/ponto/meu/comprovantes"); })
      .then((r) => setComprovantes(r?.data ?? []))
      .catch((e) => { if (e.response?.status === 404) setSemVinculo(true); else toast.error(e.message ?? "Falha ao carregar."); })
      .finally(() => setCarregando(false));
  }, []);

  const verEspelho = async () => {
    try {
      const r = await apiClient.get<Espelho>("/api/rh/ponto/meu/espelho", { params: { inicio, fim } });
      setEspelho(r.data);
    } catch (e: any) { toast.error(e.message ?? "Falha ao gerar o espelho."); }
  };

  const solicitar = async () => {
    if (!ajuste.SJustificativa.trim()) { toast.warning("Descreva a justificativa."); return; }
    try {
      await apiClient.post("/api/rh/ponto/meu/solicitar-ajuste", ajuste);
      toast.success("Solicitação enviada para aprovação.");
      setAjuste({ ...ajuste, SJustificativa: "" });
    } catch (e: any) { toast.error(e.message ?? "Falha ao enviar."); }
  };

  if (carregando) return <div className="p-6 text-muted-foreground">Carregando…</div>;
  if (semVinculo) return (
    <div className="p-6">
      <Card><CardContent className="pt-6 text-center text-muted-foreground">
        Seu login ainda não está vinculado a um funcionário do ponto. Peça ao RH para vincular seu usuário em <b>Funcionários &amp; Escala</b>.
      </CardContent></Card>
    </div>
  );

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <UserCircle className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Meu Ponto</h1>
          <p className="text-sm text-muted-foreground">{me?.nome}</p>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Meu espelho</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label>Início</Label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></div>
            <div className="space-y-1"><Label>Fim</Label><Input type="date" value={fim} onChange={(e) => setFim(e.target.value)} /></div>
            <Button onClick={verEspelho}><Search className="h-4 w-4 mr-2" />Ver</Button>
            {espelho && <span className="text-sm ml-auto"><b>Saldo banco:</b> {hm(espelho.saldoFinalMin)}</span>}
          </div>
          {espelho && (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Data</TableHead><TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Trab.</TableHead><TableHead className="text-right">Prev.</TableHead><TableHead className="text-right">Saldo</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {espelho.dias.map((d) => (
                    <TableRow key={d.data}>
                      <TableCell>{d.data.slice(8, 10)}/{d.data.slice(5, 7)}</TableCell>
                      <TableCell className="text-xs">{d.tipoDiaTexto}</TableCell>
                      <TableCell className="text-right">{hm(d.minutosTrabalhados)}</TableCell>
                      <TableCell className="text-right">{hm(d.minutosPrevistos)}</TableCell>
                      <TableCell className="text-right">{hm(d.minutosSaldo)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Meus comprovantes (últimas 48h)</CardTitle></CardHeader>
        <CardContent>
          {comprovantes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem marcações nas últimas 48h.</p>
          ) : (
            <div className="space-y-2">
              {comprovantes.map((c) => (
                <div key={c.nsrFormatado} className="flex items-center justify-between p-2 border rounded">
                  <span className="text-sm">{dt(c.dtHoraMarcacao)}</span>
                  <span className="flex items-center gap-2"><Badge variant="secondary">{c.tipoBatidaTexto}</Badge><span className="text-xs text-muted-foreground">NSR {c.nsrFormatado}</span></span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Solicitar ajuste / justificativa</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1"><Label>Data</Label><Input type="date" value={ajuste.DtReferencia} onChange={(e) => setAjuste({ ...ajuste, DtReferencia: e.target.value })} /></div>
          <div className="space-y-1">
            <Label>Tipo</Label>
            <Select value={String(ajuste.ITipoAjuste)} onValueChange={(v) => setAjuste({ ...ajuste, ITipoAjuste: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TIPOS.map((t) => <SelectItem key={t.v} value={String(t.v)}>{t.t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-3"><Label>Justificativa</Label><Input value={ajuste.SJustificativa} onChange={(e) => setAjuste({ ...ajuste, SJustificativa: e.target.value })} placeholder="Descreva o motivo…" /></div>
          <div className="sm:col-span-3"><Button onClick={solicitar}><Send className="h-4 w-4 mr-2" />Enviar solicitação</Button></div>
        </CardContent>
      </Card>
    </div>
  );
}
